import {
  CELL_ACCESS_LABELS,
  CELL_REACH_LABELS,
  PERMISSION_AREAS,
  PERMISSION_OBJECTS,
  PERMISSION_OBJECT_COPY,
  RESTRICTED_TRANSITIONS,
  RESTRICTED_TRANSITION_LABELS,
  answersOfCell,
  cellOfAnswers,
  fieldRestrictionLabel,
  isLockedCell,
  isPermissionObjectShown,
  isRestrictedTransitionShown,
  ownScopeFor,
  reachWording,
  scopeWording,
  type CellAccess,
  type CellReach,
  type ModuleState,
  type PermissionObject,
  type RestrictedTransition,
  type RoleKey,
  type UpdateRoleInput,
} from '@nest-recruit/shared';
import type { SegmentedOption } from '../../../../components/Segmented';
import type { RoleCell, RoleDetail } from '../../api/roles-api';

// Roles A (Admin redesign 2026-11-02, BRIEF item 6): the role editor's model, kept apart from the
// screen so the rules a wrong guess would quietly break are tested on their own:
// - each row is two answers, "Which ones can they open?" and "What can they do?", mapped to and
//   from the stored cell by the shared `answersOfCell` / `cellOfAnswers`;
// - a row nobody touched is never sent, and goes back exactly as stored;
// - "What you changed" and "This role now, in plain words" are worded from the cells.

/** What the editor holds while the Admin works. Cells by object, special actions by key. */
export interface RoleDraft {
  readonly label: string;
  readonly description: string;
  readonly cells: Readonly<Record<string, RoleCell>>;
  readonly transitions: Readonly<Record<string, boolean>>;
}

/** The fields of a role the editor reads: a `RoleDetail` from the server. */
export type EditableRole = Pick<
  RoleDetail,
  'id' | 'label' | 'description' | 'key' | 'baseRoleKey' | 'isSystem' | 'version' | 'assignedUserCount' | 'cells' | 'transitions'
>;

/** The line above the rows (critique: keep today's meaning of None). */
export const NONE_MEANS = 'None means hidden everywhere, even from search and direct links.';

const NO_ACCESS: Omit<RoleCell, 'objectKey'> = { level: 'none', scope: null, isReadOnly: false, restrictedFields: [] };

/** Which role's wording "their own" takes: the role's own key, or for a copy the standard role it came from. */
export function wordingKey(role: Pick<EditableRole, 'key' | 'baseRoleKey'>): RoleKey | null {
  return role.key ?? role.baseRoleKey;
}

/** The stored cell for a row. A row the role has no cell for yet (a row added later) is None. */
export function storedCellOf(role: Pick<EditableRole, 'cells'>, object: PermissionObject): RoleCell {
  return role.cells.find((cell) => cell.objectKey === object) ?? { objectKey: object, ...NO_ACCESS };
}

function storedTransitionOf(role: Pick<EditableRole, 'transitions'>, key: string): boolean {
  return role.transitions.find((transition) => transition.transitionKey === key)?.isAllowed ?? false;
}

export function draftOf(role: EditableRole): RoleDraft {
  return {
    label: role.label,
    description: role.description ?? '',
    cells: Object.fromEntries(PERMISSION_OBJECTS.map((object) => [object, storedCellOf(role, object)])),
    transitions: Object.fromEntries(RESTRICTED_TRANSITIONS.map((key) => [key, storedTransitionOf(role, key)])),
  };
}

export function draftCellOf(draft: RoleDraft, object: PermissionObject): RoleCell {
  return draft.cells[object] ?? { objectKey: object, ...NO_ACCESS };
}

// --- The two questions ------------------------------------------------------------------------

/**
 * The scope "their own" means on this row: the one stored, or the row's default for a row raised to it. Null when the
 * row has neither (review R16): only rows whose "own" the server really narrows by have a default, so "their own" is
 * offered there and nowhere else — a stored "own" always shows, so a stored value is never hidden behind an answer it
 * does not have.
 */
function ownScopeOf(role: EditableRole, object: PermissionObject): string | null {
  const stored = answersOfCell(storedCellOf(role, object), object);
  return ownScopeFor(object, stored.reach === 'own' ? stored.scope : null);
}

/** "Which ones can they open?": All · their own (worded for this row and role, where it means something) · None. */
export function reachOptions(role: EditableRole, object: PermissionObject): readonly SegmentedOption<CellReach>[] {
  const own = ownScopeOf(role, object);
  return [
    { value: 'all', label: CELL_REACH_LABELS.all },
    ...(own !== null ? [{ value: 'own' as const, label: scopeWording(object, own, wordingKey(role)) }] : []),
    { value: 'none', label: CELL_REACH_LABELS.none },
  ];
}

export const ACCESS_OPTIONS: readonly SegmentedOption<CellAccess>[] = [
  { value: 'look', label: CELL_ACCESS_LABELS.look },
  { value: 'change', label: CELL_ACCESS_LABELS.change },
];

/**
 * The cell for new answers. Always worked out from the **stored** cell, so answers put back the
 * way they were give the stored cell back exactly, odd combinations included. The hidden fields
 * the Admin set on the row ride along.
 */
function withAnswers(role: EditableRole, current: RoleCell, object: PermissionObject, reach: CellReach, access: CellAccess): RoleCell {
  const next = cellOfAnswers(storedCellOf(role, object), object, { reach, access });
  return sameFields(next.restrictedFields, current.restrictedFields) ? next : { ...next, restrictedFields: current.restrictedFields };
}

/** A new answer to "Which ones can they open?". Raising a row from None starts at Look only. */
export function withReach(role: EditableRole, current: RoleCell, object: PermissionObject, reach: CellReach): RoleCell {
  return withAnswers(role, current, object, reach, answersOfCell(current, object).access);
}

/** A new answer to "What can they do?". */
export function withAccess(role: EditableRole, current: RoleCell, object: PermissionObject, access: CellAccess): RoleCell {
  return withAnswers(role, current, object, answersOfCell(current, object).reach, access);
}

/** "Jobs in their area · Change too", "All · Look only", "None": a row's answers in words. */
export function answerText(role: EditableRole, object: PermissionObject, cell: RoleCell): string {
  const answers = answersOfCell(cell, object);
  if (answers.reach === 'none') return CELL_REACH_LABELS.none;
  return `${reachWording(object, answers, wordingKey(role))} · ${CELL_ACCESS_LABELS[answers.access]}`;
}

/** The short form for "Compare all roles": "Jobs in their area · change", "All · look", or "—". */
export function compareText(role: EditableRole, object: PermissionObject): string {
  const cell = storedCellOf(role, object);
  const answers = answersOfCell(cell, object);
  if (answers.reach === 'none') return '—';
  return `${reachWording(object, answers, wordingKey(role))} · ${answers.access === 'change' ? 'change' : 'look'}`;
}

// --- Rows shown while their part is on --------------------------------------------------------

export interface AreaRows {
  readonly key: string;
  readonly label: string;
  readonly objects: readonly PermissionObject[];
}

/** The areas and their rows to draw. A row whose part is off is left out unless "Show them anyway" is on. */
export function visibleAreas(modules: ModuleState, showHidden: boolean): readonly AreaRows[] {
  return PERMISSION_AREAS.map((area) => ({
    key: area.key,
    label: area.label,
    objects: area.objects.filter((object) => showHidden || isPermissionObjectShown(object, modules)),
  })).filter((area) => area.objects.length > 0);
}

export function visibleTransitions(modules: ModuleState, showHidden: boolean): readonly RestrictedTransition[] {
  return RESTRICTED_TRANSITIONS.filter((key) => showHidden || isRestrictedTransitionShown(key, modules));
}

/** How many rows and special actions are hidden because their part is off. */
export function hiddenCount(modules: ModuleState): number {
  return (
    PERMISSION_OBJECTS.filter((object) => !isPermissionObjectShown(object, modules)).length +
    RESTRICTED_TRANSITIONS.filter((key) => !isRestrictedTransitionShown(key, modules)).length
  );
}

// --- What changed, and what is sent -----------------------------------------------------------

function sameFields(a: readonly string[], b: readonly string[]): boolean {
  if (a.length !== b.length) return false;
  const set = new Set(a);
  return b.every((field) => set.has(field));
}

function isSameCell(a: RoleCell, b: RoleCell): boolean {
  return a.level === b.level && a.scope === b.scope && a.isReadOnly === b.isReadOnly && sameFields(a.restrictedFields, b.restrictedFields);
}

/** Whether a row on screen differs from what is stored: the row is marked, and goes in the save. */
export function isRowChanged(role: EditableRole, object: PermissionObject, cell: RoleCell): boolean {
  return !isSameCell(storedCellOf(role, object), cell);
}

export function changedObjects(role: EditableRole, draft: RoleDraft): readonly PermissionObject[] {
  return PERMISSION_OBJECTS.filter((object) => isRowChanged(role, object, draftCellOf(draft, object)));
}

export function changedTransitions(role: EditableRole, draft: RoleDraft): readonly RestrictedTransition[] {
  return RESTRICTED_TRANSITIONS.filter((key) => storedTransitionOf(role, key) !== (draft.transitions[key] ?? false));
}

const isLabelChanged = (role: EditableRole, draft: RoleDraft): boolean => draft.label.trim() !== role.label;
const isDescriptionChanged = (role: EditableRole, draft: RoleDraft): boolean => draft.description.trim() !== (role.description ?? '');

/** How many things the save would change: the name, the description, each row and each special action. */
export function changeCount(role: EditableRole, draft: RoleDraft): number {
  return (
    (isLabelChanged(role, draft) ? 1 : 0) +
    (isDescriptionChanged(role, draft) ? 1 : 0) +
    changedObjects(role, draft).length +
    changedTransitions(role, draft).length
  );
}

function cellInput(cell: RoleCell): NonNullable<UpdateRoleInput['cells']>[number] {
  return { objectKey: cell.objectKey, level: cell.level, scope: cell.scope, isReadOnly: cell.isReadOnly, restrictedFields: [...cell.restrictedFields] };
}

/**
 * The PATCH for a save: only what changed. A row nobody touched is not sent, so the server keeps it
 * exactly as stored. Two exceptions, both the server's rules:
 * - `version` always goes, so a save over somebody else's newer one is refused (409);
 * - a locked row (Admin's People and roles) goes with any cell change, as stored, because the
 *   server refuses a cell list that leaves it out.
 * Null when there is nothing to save.
 */
export function buildUpdate(role: EditableRole, draft: RoleDraft): UpdateRoleInput | null {
  if (changeCount(role, draft) === 0) return null;
  const objects = changedObjects(role, draft);
  const locked = PERMISSION_OBJECTS.filter((object) => isLockedCell(role.key, object) && !objects.includes(object));
  const transitions = changedTransitions(role, draft);
  return {
    version: role.version,
    ...(isLabelChanged(role, draft) ? { label: draft.label.trim() } : {}),
    ...(isDescriptionChanged(role, draft) ? { description: draft.description.trim() === '' ? null : draft.description.trim() } : {}),
    ...(objects.length > 0
      ? { cells: [...objects.map((object) => cellInput(draftCellOf(draft, object))), ...locked.map((object) => cellInput(storedCellOf(role, object)))] }
      : {}),
    ...(transitions.length > 0 ? { transitions: transitions.map((key) => ({ transitionKey: key, isAllowed: draft.transitions[key] ?? false })) } : {}),
  };
}

// --- "What you changed" -----------------------------------------------------------------------

export function listOf(items: readonly string[]): string {
  if (items.length <= 1) return items.join('');
  return `${items.slice(0, -1).join(', ')} and ${items.at(-1) ?? ''}`;
}

/** Field names as the catalogue writes them ("NID and personal identity documents" keeps its capitals). */
const fieldsText = (fields: readonly string[]): string => listOf(fields.map((field) => fieldRestrictionLabel(field)));

/** One line per change, in words, as the Admin makes them. */
export function changeLines(role: EditableRole, draft: RoleDraft): readonly string[] {
  const lines: string[] = [];
  if (isLabelChanged(role, draft)) lines.push(`Name: ${role.label} → ${draft.label.trim()}`);
  if (isDescriptionChanged(role, draft)) {
    lines.push(draft.description.trim() === '' ? 'What this role is for: cleared' : `What this role is for: now “${draft.description.trim()}”`);
  }
  for (const object of changedObjects(role, draft)) {
    const label = PERMISSION_OBJECT_COPY[object].label;
    const before = storedCellOf(role, object);
    const after = draftCellOf(draft, object);
    const beforeText = answerText(role, object, before);
    const afterText = answerText(role, object, after);
    if (beforeText !== afterText) lines.push(`${label}: ${beforeText} → ${afterText}`);
    const added = after.restrictedFields.filter((field) => !before.restrictedFields.includes(field));
    const removed = before.restrictedFields.filter((field) => !after.restrictedFields.includes(field));
    if (added.length > 0) lines.push(`${label}: now shows — for ${fieldsText(added)}`);
    if (removed.length > 0) lines.push(`${label}: shows ${fieldsText(removed)} again`);
    // A row whose words read the same but whose stored form changed (a scope filled in).
    if (beforeText === afterText && added.length === 0 && removed.length === 0) lines.push(`${label}: tidied up, same access`);
  }
  for (const key of changedTransitions(role, draft)) {
    lines.push(`${RESTRICTED_TRANSITION_LABELS[key]}: ${draft.transitions[key] ? 'now allowed' : 'no longer allowed'}`);
  }
  return lines;
}

// --- "This role now, in plain words" ----------------------------------------------------------

const lowerFirst = (text: string): string => text.charAt(0).toLowerCase() + text.slice(1);

/**
 * The role as sentences, from the cells as they are on screen (saved or not). Only the rows and
 * special actions drawn are described, so the summary and the editor never disagree.
 */
export function plainWords(
  role: EditableRole,
  draft: RoleDraft,
  objects: readonly PermissionObject[],
  transitions: readonly RestrictedTransition[],
): readonly string[] {
  const key = wordingKey(role);
  const allChange: string[] = [];
  const allLook: string[] = [];
  const ownChange: string[] = [];
  const ownLook: string[] = [];
  const none: PermissionObject[] = [];
  const hidden: string[] = [];

  for (const object of objects) {
    const cell = draftCellOf(draft, object);
    const answers = answersOfCell(cell, object);
    const label = PERMISSION_OBJECT_COPY[object].label;
    if (answers.reach === 'none') {
      none.push(object);
      continue;
    }
    if (answers.reach === 'all') (answers.access === 'change' ? allChange : allLook).push(label);
    else (answers.access === 'change' ? ownChange : ownLook).push(`${label} (${lowerFirst(reachWording(object, answers, key))})`);
    hidden.push(...cell.restrictedFields);
  }

  const sentences: string[] = [];
  if (allChange.length + allLook.length + ownChange.length + ownLook.length === 0) {
    sentences.push("Can't open anything yet. Pick All or their own on a row to give access.");
  }
  if (allChange.length > 0) sentences.push(`Sees and changes all of these: ${listOf(allChange)}.`);
  if (allLook.length > 0) sentences.push(`Sees all of these, but can't change them: ${listOf(allLook)}.`);
  // Review R16: "their own" was not always true (a recruiter's rows reach their area); each row says which in brackets.
  if (ownChange.length > 0) sentences.push(`Sees and changes only some of these: ${listOf(ownChange)}.`);
  if (ownLook.length > 0) sentences.push(`Sees only some of these, and can't change them: ${listOf(ownLook)}.`);
  if (none.length > 0 && none.length < objects.length) sentences.push(`Can't open: ${listOf(noneLabels(none, objects))}.`);
  if (hidden.length > 0) sentences.push(`Shows — instead of ${fieldsText([...new Set(hidden)])}.`);

  const allowed = transitions.filter((transition) => draft.transitions[transition]);
  sentences.push(allowed.length > 0 ? `Special actions: ${listOf(allowed.map((transition) => RESTRICTED_TRANSITION_LABELS[transition]))}.` : 'No special actions.');
  return sentences;
}

/** "Can't open" names the rows, except that a whole Admin area of None reads "anything in Admin". */
function noneLabels(none: readonly PermissionObject[], drawn: readonly PermissionObject[]): readonly string[] {
  const adminArea: readonly PermissionObject[] = PERMISSION_AREAS.find((area) => area.key === 'admin')?.objects ?? [];
  const adminDrawn = adminArea.filter((object) => drawn.includes(object));
  const isAdminClosed = adminDrawn.length > 1 && adminDrawn.every((object) => none.includes(object));
  const rest = none.filter((object) => !(isAdminClosed && adminDrawn.includes(object))).map((object) => PERMISSION_OBJECT_COPY[object].label);
  return isAdminClosed ? [...rest, 'anything in Admin'] : rest;
}
